import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Share, Download, PlayArrow, PlayCircle } from '@mui/icons-material';
import { HighlightClip } from '../../models/HighlightClip';
import { DemoStatusResponse } from '../../services/HighlightsService';
import { Match } from '../../models/Match';
import PlayerImage from '../../components/PlayerImage';
import { mapMappings } from '../../Constants';
import { track } from '../../utils/track';
import styles from './MatchPage2.module.css';

// Shared with the old clips tab so the volume follows the user across designs.
const VOLUME_STORAGE_KEY = 'powerstats-clip-volume';
const MUTED_STORAGE_KEY = 'powerstats-clip-muted';

interface Match2ClipsProps {
    matchId: string;
    match: Match; // series-level (matches[0]) for team names/rosters
    selectedMatch: Match | null;
    clips: HighlightClip[];
    demoStatus: DemoStatusResponse | null;
    // Maps (no '*') with a 2D replay, and the jump to a clip's round there (3 s before the
    // player's first kill). Matched on mapName: a clip's gameId is "<matchId>_<n>", not the replay's.
    replayMapNames?: string[];
    onViewInReplay?: (clip: HighlightClip) => void;
}

const formatMapName = (mapName: string | undefined | null): string => {
    if (!mapName) return 'Unknown';
    const clean = mapName.replace('*', '');
    const mapped = mapMappings.get(clean);
    if (mapped) return mapped;
    const noPrefix = clean.replace('de_', '');
    return noPrefix.charAt(0).toUpperCase() + noPrefix.slice(1);
};

const getClutchLabel = (type: string): string => {
    const match = type.toLowerCase().match(/^clutch(\dv\d)$/);
    return match ? match[1] : 'Clutch';
};

// Kill/clutch badge with the redesign's gradient palette.
const Badge: React.FC<{ clip: HighlightClip; small?: boolean }> = ({ clip, small }) => {
    const type = clip.highlightType?.toLowerCase() ?? '';
    let cls = '';
    let label = `${clip.killCount}K`;
    if (type.startsWith('clutch2v')) {
        cls = styles.clipBadgeClutch2;
        label = getClutchLabel(type);
    } else if (type.startsWith('clutch')) {
        cls = styles.clipBadgeClutch;
        label = getClutchLabel(type);
    } else if (clip.killCount >= 5) {
        cls = styles.clipBadgeAce;
        label = 'ACE';
    } else if (clip.killCount >= 4) {
        cls = styles.clipBadgeQuad;
    } else if (clip.killCount >= 3) {
        cls = styles.clipBadgeTriple;
    }
    return (
        <span className={`${styles.clipBadge} ${small ? styles.clipBadgeSmall : ''} ${cls}`}>{label}</span>
    );
};

// Clips tab: team filter buttons, main player with share/download, list sidebar.
// Fetch/URL logic mirrors the old MatchClips; only the layout is redesigned.
const Match2Clips: React.FC<Match2ClipsProps> = ({ matchId, match, selectedMatch, clips, demoStatus, replayMapNames = [], onViewInReplay }) => {
    const { t } = useTranslation('pages');
    const [filterTeam, setFilterTeam] = useState<'all' | 'team1' | 'team2'>('all');
    const [selectedClip, setSelectedClip] = useState<HighlightClip | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        track('clips-tab-view', { matchId, source: 'match2' });
    }, [matchId]);

    const handleVideoLoaded = useCallback(() => {
        if (videoRef.current) {
            const savedVolume = localStorage.getItem(VOLUME_STORAGE_KEY);
            videoRef.current.volume = savedVolume !== null ? parseFloat(savedVolume) : 1;
            videoRef.current.muted = localStorage.getItem(MUTED_STORAGE_KEY) === 'true';
        }
    }, []);

    const handleVolumeChange = useCallback(() => {
        if (videoRef.current) {
            localStorage.setItem(VOLUME_STORAGE_KEY, videoRef.current.volume.toString());
            localStorage.setItem(MUTED_STORAGE_KEY, videoRef.current.muted.toString());
        }
    }, []);

    const handleVideoPlay = useCallback(() => {
        if (selectedClip) {
            track('clip-play', {
                source: 'match2',
                matchId,
                clipId: selectedClip.id,
                type: selectedClip.highlightType,
                map: selectedClip.mapName,
            });
        }
    }, [matchId, selectedClip]);

    const isPlayerOnTeam = (steamId: string, teamPlayerIds: string[] | undefined): boolean =>
        !!teamPlayerIds && teamPlayerIds.includes(steamId);

    // Map filter first (selected map pill), then team filter.
    const mapFilteredClips = useMemo(() => {
        if (selectedMatch === null) return clips;
        const currentMapName = selectedMatch.mapName.replace('*', '');
        return clips.filter(clip => (clip.mapName || '') === currentMapName);
    }, [clips, selectedMatch]);

    const filteredClips = useMemo(() => {
        return mapFilteredClips.filter(clip => {
            if (filterTeam === 'all') return true;
            if (filterTeam === 'team1') return isPlayerOnTeam(clip.steamId, match.team1playerids);
            if (filterTeam === 'team2') return isPlayerOnTeam(clip.steamId, match.team2playerids);
            return true;
        });
    }, [mapFilteredClips, filterTeam, match.team1playerids, match.team2playerids]);

    const team1ClipCount = mapFilteredClips.filter(c => isPlayerOnTeam(c.steamId, match.team1playerids)).length;
    const team2ClipCount = mapFilteredClips.filter(c => isPlayerOnTeam(c.steamId, match.team2playerids)).length;

    // Keep the selected clip valid when the map/team filter changes.
    useEffect(() => {
        if (filteredClips.length > 0) {
            if (!selectedClip || !filteredClips.find(c => c.id === selectedClip.id)) {
                setSelectedClip(filteredClips[0]);
            }
        } else {
            setSelectedClip(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredClips]);

    // Compact status-derived empty state (full diagnostics live on the old page).
    if (clips.length === 0) {
        let icon = '🎬';
        let title = 'Ingen highlights';
        let message = 'Der er ingen highlights for denne kamp endnu.';
        if (demoStatus && demoStatus.games.length > 0) {
            const games = demoStatus.games;
            if (games.every(g => g.status === 'broken')) {
                icon = '🔧';
                title = 'Demo utilgængelig';
                message = 'Demofilen for denne kamp er desværre beskadiget eller utilgængelig.';
            } else if (games.every(g => g.status === 'failed')) {
                icon = '⚠️';
                title = 'Kunne ikke generere highlights';
                message = 'Der opstod en fejl under behandling af demofilen.';
            } else if (games.some(g => g.status === 'processing')) {
                icon = '⏳';
                title = 'Genererer highlights...';
                message = 'Highlights er ved at blive genereret. Dette kan tage et par minutter.';
            } else if (games.some(g => g.status === 'pending')) {
                icon = '📋';
                title = 'Venter i kø';
                message = 'Demoen er sat i kø og venter på at blive behandlet.';
            } else {
                title = 'Ingen highlights fundet';
                message = 'Der blev ikke fundet nogle fremragende øjeblikke i denne kamp.';
            }
        } else {
            title = 'Ikke i kø';
            message = 'Denne kamp er ikke sat i kø til highlight-generering endnu.';
        }
        return (
            <div className={styles.fadeIn} style={{ marginTop: 18 }}>
                <div className={styles.clipsEmpty}>
                    <div className={styles.clipsEmptyIcon}>{icon}</div>
                    <h3 className={styles.clipsEmptyTitle}>{title}</h3>
                    <p className={styles.clipsEmptyText}>{message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.fadeIn} style={{ marginTop: 18 }}>
            <div className={styles.clipsFilters}>
                <div className={styles.clipsFilterGroup}>
                    <button
                        className={`${styles.clipFilterBtn} ${filterTeam === 'all' ? styles.clipFilterBtnActive : ''}`}
                        onClick={() => setFilterTeam('all')}
                    >
                        {t('match2.all')}
                        <span className={styles.clipFilterCount}>{mapFilteredClips.length}</span>
                    </button>
                    <button
                        className={`${styles.clipFilterBtn} ${filterTeam === 'team1' ? styles.clipFilterBtnActive : ''}`}
                        onClick={() => setFilterTeam('team1')}
                    >
                        {match.team1.teamLogo && (
                            <img src={match.team1.teamLogo} alt="" className={styles.clipFilterLogo} />
                        )}
                        {match.team1.teamName}
                        <span className={styles.clipFilterCount}>{team1ClipCount}</span>
                    </button>
                    <button
                        className={`${styles.clipFilterBtn} ${filterTeam === 'team2' ? styles.clipFilterBtnActive : ''}`}
                        onClick={() => setFilterTeam('team2')}
                    >
                        {match.team2.teamLogo && (
                            <img src={match.team2.teamLogo} alt="" className={styles.clipFilterLogo} />
                        )}
                        {match.team2.teamName}
                        <span className={styles.clipFilterCount}>{team2ClipCount}</span>
                    </button>
                </div>
            </div>

            <div className={styles.clipsGrid}>
                {/* Main player */}
                <div className={styles.clipsMain}>
                    {selectedClip ? (
                        <>
                            {selectedClip.clipUrl ? (
                                <video
                                    ref={videoRef}
                                    key={selectedClip.id}
                                    className={styles.clipsVideo}
                                    controls
                                    autoPlay
                                    playsInline
                                    src={selectedClip.clipUrl}
                                    poster={selectedClip.thumbnailUrl || undefined}
                                    onLoadedData={handleVideoLoaded}
                                    onVolumeChange={handleVolumeChange}
                                    onPlay={handleVideoPlay}
                                >
                                    Din browser understøtter ikke video afspilning.
                                </video>
                            ) : (
                                <div className={styles.clipsVideoPlaceholder}>
                                    <PlayArrow sx={{ fontSize: 44, color: 'rgba(255,255,255,0.35)' }} />
                                    <span>Video ikke tilgængelig</span>
                                </div>
                            )}
                            <div className={styles.clipsInfo}>
                                <div className={styles.clipsInfoLeft}>
                                    <Link to={`/player/${selectedClip.steamId}`} className={styles.clipsPlayerLink}>
                                        {selectedClip.playerName}
                                    </Link>
                                    <div className={styles.clipsMetaRow}>
                                        <Badge clip={selectedClip} />
                                        <span className={styles.clipsMetaMap}>{formatMapName(selectedClip.mapName)}</span>
                                        <span className={styles.clipsMetaRound}>
                                            {t('match2.round')} {selectedClip.roundNr}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.clipsActions}>
                                    {onViewInReplay && replayMapNames.includes(selectedClip.mapName || '') && (
                                        <button
                                            className={styles.clipActionBtn}
                                            title={t('match2.viewIn2d')}
                                            onClick={() => onViewInReplay(selectedClip)}
                                        >
                                            <PlayCircle sx={{ fontSize: 19 }} />
                                        </button>
                                    )}
                                    {selectedClip.clipUrl && (
                                        <button
                                            className={styles.clipActionBtn}
                                            title={t('match2.share')}
                                            onClick={() => {
                                                navigator.clipboard.writeText(selectedClip.clipUrl);
                                                alert('Link kopieret!');
                                            }}
                                        >
                                            <Share sx={{ fontSize: 19 }} />
                                        </button>
                                    )}
                                    {selectedClip.clipUrl && (
                                        <a
                                            href={selectedClip.clipUrl}
                                            download
                                            className={styles.clipActionBtn}
                                            title={t('match2.download')}
                                        >
                                            <Download sx={{ fontSize: 19 }} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={styles.clipsVideoPlaceholder}>
                            <PlayArrow sx={{ fontSize: 44, color: 'rgba(255,255,255,0.35)' }} />
                        </div>
                    )}
                </div>

                {/* Clip list sidebar */}
                <div className={styles.clipsSidebar}>
                    {filteredClips.length === 0 && (
                        <div className={styles.clipsSidebarEmpty}>{t('match2.tabClips')}: 0</div>
                    )}
                    {filteredClips.map(clip => {
                        const isTeam1 = isPlayerOnTeam(clip.steamId, match.team1playerids);
                        const teamLogo = isTeam1 ? match.team1.teamLogo : match.team2.teamLogo;
                        return (
                            <div
                                key={clip.id}
                                className={`${styles.clipItem} ${selectedClip?.id === clip.id ? styles.clipItemActive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedClip(clip)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedClip(clip);
                                    }
                                }}
                            >
                                <div className={styles.clipThumb}>
                                    {clip.thumbnailUrl
                                        ? <img src={clip.thumbnailUrl} alt="" className={styles.clipThumbImg} />
                                        : <PlayArrow sx={{ fontSize: 26 }} />}
                                    {teamLogo && <img src={teamLogo} alt="" className={styles.clipThumbLogo} />}
                                    <span className={styles.clipThumbDur}>{clip.clipLength.toFixed(0)}s</span>
                                </div>
                                <div className={styles.clipItemInfo}>
                                    <Badge clip={clip} small />
                                    <span className={styles.clipItemRound}>{t('match2.round')} {clip.roundNr}</span>
                                    <span className={styles.clipItemMap}>{formatMapName(clip.mapName)}</span>
                                </div>
                                <div className={styles.clipItemPlayer}>
                                    <span className={styles.clipItemPlayerName}>{clip.playerName}</span>
                                    <Link
                                        to={`/player/${clip.steamId}`}
                                        className={styles.clipItemAvatar}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        {/* Dated to the match, so an old clip shows the player as they looked then. */}
                                        <PlayerImage id={clip.steamId} width={40} at={match.match_time} />
                                    </Link>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Match2Clips;
